const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

const scrollAnimationElements = new Map();
const scrollAnimationObserverTargets = new Map();
const zoomAnimationElements = new Map();
let zoomAnimationObserver;
let zoomAnimationFrame;
let zoomScrollListening = false;

function getElementsWithin(rootEl, className) {
  const elements = Array.from(rootEl.getElementsByClassName(className));
  if (rootEl.matches?.(`.${className}`)) elements.unshift(rootEl);
  return elements;
}

function onIntersection(entries) {
  entries.forEach((entry, index) => {
    const elementTarget = entry.target;
    if (entry.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      const targetObserver = scrollAnimationElements.get(elementTarget);
      targetObserver?.unobserve(elementTarget);
      scrollAnimationElements.delete(elementTarget);
      const observedElements = scrollAnimationObserverTargets.get(targetObserver);
      observedElements?.delete(elementTarget);
      if (observedElements?.size === 0) {
        targetObserver.disconnect();
        scrollAnimationObserverTargets.delete(targetObserver);
      }
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = getElementsWithin(rootEl, SCROLL_ANIMATION_TRIGGER_CLASSNAME);
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const newElements = animationTriggerElements.filter((element) => !scrollAnimationElements.has(element));
  if (newElements.length === 0) return;

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  const observedElements = new Set(newElements);
  scrollAnimationObserverTargets.set(observer, observedElements);
  newElements.forEach((element) => {
    scrollAnimationElements.set(element, observer);
    observer.observe(element);
  });
}

function updateZoomAnimation(element) {
  const scaleAmount = 0.2 / 100;
  element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
}

function updateVisibleZoomAnimations() {
  zoomAnimationFrame = undefined;
  zoomAnimationElements.forEach((record, element) => {
    if (!element.isConnected) {
      removeZoomAnimationElement(element);
    } else if (record.isVisible) {
      updateZoomAnimation(element);
    }
  });
}

function queueZoomAnimationUpdate() {
  if (zoomAnimationFrame === undefined) {
    zoomAnimationFrame = window.requestAnimationFrame(updateVisibleZoomAnimations);
  }
}

function removeZoomAnimationElement(element) {
  if (!zoomAnimationElements.delete(element)) return;
  zoomAnimationObserver?.unobserve(element);

  if (zoomAnimationElements.size > 0) return;

  zoomAnimationObserver?.disconnect();
  zoomAnimationObserver = undefined;
  if (zoomScrollListening) {
    window.removeEventListener('scroll', queueZoomAnimationUpdate);
    zoomScrollListening = false;
  }
  if (zoomAnimationFrame !== undefined) {
    window.cancelAnimationFrame(zoomAnimationFrame);
    zoomAnimationFrame = undefined;
  }
}

function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = getElementsWithin(rootEl, SCROLL_ZOOM_IN_TRIGGER_CLASSNAME);
  if (animationTriggerElements.length === 0) return;

  if (!zoomAnimationObserver) {
    zoomAnimationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const record = zoomAnimationElements.get(entry.target);
        if (record) record.isVisible = entry.isIntersecting;
      });
    });
  }

  animationTriggerElements.forEach((element) => {
    const existingRecord = zoomAnimationElements.get(element);
    if (existingRecord) {
      updateZoomAnimation(element);
      return;
    }

    zoomAnimationElements.set(element, { isVisible: false });
    zoomAnimationObserver.observe(element);
    updateZoomAnimation(element);
  });

  if (!zoomScrollListening) {
    window.addEventListener('scroll', queueZoomAnimationUpdate, { passive: true });
    zoomScrollListening = true;
  }
}

function removeAnimationsWithin(rootEl) {
  getElementsWithin(rootEl, SCROLL_ANIMATION_TRIGGER_CLASSNAME).forEach((element) => {
    const observer = scrollAnimationElements.get(element);
    if (!observer) return;
    observer.unobserve(element);
    scrollAnimationElements.delete(element);
    const observedElements = scrollAnimationObserverTargets.get(observer);
    observedElements?.delete(element);
    if (observedElements?.size === 0) {
      observer.disconnect();
      scrollAnimationObserverTargets.delete(observer);
    }
  });

  getElementsWithin(rootEl, SCROLL_ZOOM_IN_TRIGGER_CLASSNAME).forEach(removeZoomAnimationElement);
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => removeAnimationsWithin(event.target));
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger(document);
  });
}
